import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertBanner,
    Avatar,
    Button,
    DataTable,
    Modal,
    ModalFeedback,
    PopoverMenu,
    SparkIcon,
    SparkModalPortal,
    TableToolbarContent,
    TagColored,
    getOverlayPortalParent,
    useAnchoredPanelPosition,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import AddUserModal from './AddUserModal.jsx';
import UserFilters from './UserFilters.jsx';
import UserRoleSelect, { OWNER_ROLE_LABEL, ROLE_TAG_VARIANT } from './UserRoleSelect.jsx';
import RolesPermissionsMatrix from './RolesPermissionsMatrix.jsx';
import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { data_from_server_post } from '@/shared/http/client';
import { formatDateTime } from '@/shared/utils/helpers';
import { useAuth } from '@/features/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import '@/features/settings/components/settings.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function getEmailLocalPart(email) {
    return (email || '').split('@')[0] || '';
}

function getLastLoginTime(user) {
    const value = user?.last_login;
    if (!value) return 0;
    if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const n = Number(value);
        return n < 1e12 ? n * 1000 : n; // seconds vs millis
    }
    const time = new Date(String(value).trim().replace(' ', 'T')).getTime();
    return Number.isNaN(time) ? 0 : time;
}

function UserRowActions({ onRemove, canRemove = true }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const close = () => setOpen(false);

    useOutsideClick([wrapRef, panelRef], close, open);
    useEscapeKey(close, open);
    const panelStyle = useAnchoredPanelPosition(wrapRef, panelRef, open, 'end');

    // "Remove user" is the only row action, so the whole menu goes away on
    // rows the backend refuses to remove (see UserManagement's columns).
    if (!canRemove) return null;

    const panel = open ? (
        <div
            ref={panelRef}
            className="settings-users__row-actions-panel"
            style={panelStyle}
        >
            <PopoverMenu
                tip={false}
                items={[
                    {
                        label: 'Remove user',
                        showPrefixIcon: true,
                        prefixIconSlug: 'trash',
                        disableWhenReadOnly: true,
                        onClick: () => {
                            setOpen(false);
                            onRemove?.();
                        },
                    },
                ]}
            />
        </div>
    ) : null;

    return (
        <div ref={wrapRef} className="settings-users__row-actions">
            <Button
                variant="ghost"
                size="small"
                aria-label="User actions"
                icon={<SparkIcon slug="dots" size="small" />}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((prev) => !prev);
                }}
            />
            {open ? createPortal(panel, getOverlayPortalParent(wrapRef.current)) : null}
        </div>
    );
}

export default function UserManagement() {
    const { user: currentUser, workspace, refresh, platformRole, isAuthEnabled } = useAuth();
    // Mirrors the backend `is_god`: auth-disabled installs are god.
    const isGod = !isAuthEnabled || platformRole === 'god';
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [workspaceInfo, setWorkspaceInfo] = useState(null); // {id, name}
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [rolesModalOpen, setRolesModalOpen] = useState(false);
    const [barVisible, setBarVisible] = useState(false);
    const [lastAddedName, setLastAddedName] = useState('');
    const [filters, setFilters] = useState({ query: '', role: '' });
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [userToRemove, setUserToRemove] = useState(null);
    const [addUserError, setAddUserError] = useState('');
    const [selfDemoteUser, setSelfDemoteUser] = useState(null);
    const [selfDemoteRole, setSelfDemoteRole] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const filteredUsers = users.filter((u) => {
        const matchesQuery = filters.query
            ? (u.name?.toLowerCase().includes(filters.query.toLowerCase())
                || u.email?.toLowerCase().includes(filters.query.toLowerCase()))
            : true;
        const matchesRole = filters.role ? u.role === filters.role : true;
        return matchesQuery && matchesRole;
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        const ta = getLastLoginTime(a);
        const tb = getLastLoginTime(b);
        if (ta !== tb) return tb - ta; // most recent login first, never-logged-in last
        return (a.name || '').localeCompare(b.name || '');
    });

    const pagedUsers = useMemo(
        () => sortedUsers.slice((page - 1) * pageSize, page * pageSize),
        [sortedUsers, page, pageSize]
    );

    const activeFilterCount = (filters.role ? 1 : 0) + (filters.query ? 1 : 0);

    const openRemoveUserModal = (user) => {
        setUserToRemove(user);
        setRemoveModalOpen(true);
    };

    const confirmRemoveUser = () => {
        if (!userToRemove) return;
        data_from_server_post('users/remove', userToRemove.email, () => {
            // Remove from local state immediately (membership removed from workspace).
            setUsers((prev) => prev.filter((u) => u.email !== userToRemove.email));
            setRemoveModalOpen(false);
            setUserToRemove(null);
        });
    };

    const handleResendInvitation = (invitationId, email) => {
        data_from_server_post(
            `workspaces/${workspaceInfo?.id || workspace?.id}/invitations/${invitationId}/resend`,
            {},
            () => {
                // Show a brief success via refetch (no structural change).
                alert(`Invitation resent to ${email}.`);
            },
            (err) => {
                alert(typeof err === 'string' ? err : 'Failed to resend invitation.');
            },
        );
    };

    const handleRevokeInvitation = (invitationId, email) => {
        data_from_server_post(
            `workspaces/${workspaceInfo?.id || workspace?.id}/invitations/${invitationId}/revoke`,
            {},
            () => {
                setUsers((prev) => prev.filter((u) => u.invitation_id !== invitationId));
            },
            (err) => {
                alert(typeof err === 'string' ? err : 'Failed to revoke invitation.');
            },
        );
    };

    const handleChangeRole = (userId, role) => {
        setUsers((prev) => prev.map((u) => ((u._id === userId || u.id === userId) ? { ...u, role } : u)));
    };

    const requestSetRole = (u, newRole, isSelfDemotion = false) => {
        data_from_server_post('users/set-role', { email: u.email, role: newRole }, () => {
            const rowKey = u._id || u.email;
            handleChangeRole(rowKey, newRole);
            if (isSelfDemotion) {
                try { refresh && refresh(); } catch (_) { /* ignore */ }
                navigate('/');
                return;
            }
            refetchUsers();
        });
    };

    const maybeConfirmSelfDemotion = (u, newRole) => {
        const isSelf = !!(currentUser?.email && u.email === currentUser.email);
        const isDemotingFromAdmin = u.role === 'Admin' && newRole !== 'Admin';
        if (isSelf && isDemotingFromAdmin) {
            setSelfDemoteUser(u);
            setSelfDemoteRole(newRole);
            return;
        }
        requestSetRole(u, newRole, false);
    };

    const refetchUsers = () => {
        data_from_server_post('users/list', {}, (res) => {
            setUsers(res?.users || []);
            if (res?.workspace) setWorkspaceInfo(res.workspace);
        }, (err) => {
            const msg = typeof err === 'string' ? err : '';
            if (msg && msg.toLowerCase().includes('admin role required')) {
                return;
            }
        });
    };

    useEffect(() => {
        refetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPage(1);
    }, [filters.query, filters.role, pageSize]);

    const columns = [
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            render: (_, u) => {
                const displayName = u.name || getEmailLocalPart(u.email);
                const isInvited = u.status === 'invited';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size="medium"
                            type={u.picture ? 'image' : 'initials'}
                            src={u.picture || undefined}
                            alt={displayName}
                            primaryText={displayName}
                            secondaryText={u.email || ''}
                            showContent
                        />
                        {isInvited && (
                            <Tooltip title={u.expires_at ? `Expires ${new Date(u.expires_at).toLocaleDateString()}` : 'Pending'}>
                                <Tag color="orange" style={{ marginLeft: 4 }}>Pending</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Last login',
            key: 'last_login',
            dataIndex: 'last_login',
            render: (value, u) => {
                if (u.status === 'invited') return <span style={{ color: '#9ca3af' }}>Pending acceptance</span>;
                return value ? formatDateTime(value) : '-';
            },
        },
        {
            title: 'Role',
            key: 'role',
            dataIndex: 'role',
            width: 160,
            render: (role, u) => {
                if (u.status === 'invited') {
                    // Invited rows: show the role but don't allow changing it here
                    // (the role is set on the invitation and applied at acceptance).
                    return <span style={{ color: '#6b7280', fontSize: 13 }}>{role}</span>;
                }
                if (u.is_owner) {
                    // The owner is always an Admin; their role only changes through
                    // Settings > Workspace > Transfer ownership.  The wrapper keeps
                    // the tag at its intrinsic width: DataTable stretches a cell's
                    // direct child to the full column.
                    return (
                        <span className="settings-users__role-cell">
                            <TagColored
                                className="settings-users__role-tag"
                                variant={ROLE_TAG_VARIANT[OWNER_ROLE_LABEL]}
                                label={OWNER_ROLE_LABEL}
                            />
                        </span>
                    );
                }
                return (
                    <UserRoleSelect
                        role={role}
                        onChange={(nextRole) => maybeConfirmSelfDemotion(u, nextRole)}
                    />
                );
            },
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, u) => {
                if (u.status === 'invited' && u.invitation_id) {
                    return (
                        <div style={{ display: 'flex', gap: 4 }}>
                            <button
                                type="button"
                                style={{ fontSize: 12, color: '#1677ff', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                                onClick={() => handleResendInvitation(u.invitation_id, u.email)}
                            >
                                Resend
                            </button>
                            <button
                                type="button"
                                style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                                onClick={() => handleRevokeInvitation(u.invitation_id, u.email)}
                            >
                                Revoke
                            </button>
                        </div>
                    );
                }
                // Nobody removes the owner (transfer ownership first); only gods may remove themselves.
                const isSelf = !!(currentUser?.email && u.email === currentUser.email);
                const canRemove = !u.is_owner && (!isSelf || isGod);
                return <UserRowActions canRemove={canRemove} onRemove={() => openRemoveUserModal(u)} />;
            },
        },
    ];

    const workspaceName = workspaceInfo?.name || workspace?.name;

    return (
        <>
            <SettingsSectionCard
                iconSlug="user"
                title={workspaceName ? `User Management - ${workspaceName}` : 'User Management'}
                headerExtra={(
                    <>
                        <Button
                            variant="tertiary"
                            size="medium"
                            onClick={() => setRolesModalOpen(true)}
                        >
                            View Roles & Permissions
                        </Button>
                        <Button
                            variant="primary"
                            size="medium"
                            showStartIcon
                            disableWhenReadOnly
                            onClick={() => setIsAddModalOpen(true)}
                        >
                            Add Users
                        </Button>
                    </>
                )}
            >
                <div className="settings-users">
                    <UserFilters value={filters} onChange={setFilters} />
                    <div className="settings-users__table-block">
                        <div className="settings-users__toolbar">
                            <TableToolbarContent
                                title={`Users (${sortedUsers.length})`}
                                showInfoIconTitle={false}
                                showSubtitle={false}
                            />
                            <Button
                                variant="tertiary"
                                size="small"
                                startIcon={<SparkIcon slug="filter" size="small" />}
                            >
                                {`Filter Metrics (${activeFilterCount})`}
                            </Button>
                        </div>
                        <DataTable
                            rowKey={(record) => String(record._id || record.email)}
                            columns={columns}
                            dataSource={pagedUsers}
                            rowHeight={60}
                            disableHoverBackground
                            pagination={{
                                current: page,
                                pageSize,
                                total: sortedUsers.length,
                                pageSizeOptions: PAGE_SIZE_OPTIONS,
                                onChange: setPage,
                                onPageSizeChange: (size) => {
                                    setPageSize(size);
                                    setPage(1);
                                },
                            }}
                            emptyText="No users found"
                        />
                    </div>
                </div>
            </SettingsSectionCard>

            {barVisible && (
                <div className="settings-users__banner">
                    <AlertBanner
                        feedback="success"
                        text={(
                            <span>
                                <b>{lastAddedName}</b>
                                {lastAddedName.includes(',') ? ' were' : ' was'}
                                {' '}
                                invited. They stay pending until they accept the email.
                            </span>
                        )}
                        buttonType="text"
                        showButton={false}
                        showCloseButton
                        onCloseClick={() => setBarVisible(false)}
                    />
                </div>
            )}

            <AddUserModal
                open={isAddModalOpen}
                onCancel={() => { setAddUserError(''); setIsAddModalOpen(false); }}
                onSubmit={async (values) => {
                    setAddUserError('');
                    const invites = values.invites || [];
                    const addedEmails = [];
                    const failures = [];
                    for (const { email, role } of invites) {
                        // eslint-disable-next-line no-await-in-loop
                        const res = await data_from_server_post(
                            'users/add',
                            { email, role },
                            undefined,
                            (err) => {
                                const msg = typeof err === 'string' && err ? err : 'Failed to invite';
                                failures.push(`${email}: ${msg}`);
                            },
                        );
                        // Phase 4: response is {invitation_id, email, role, fulfilled, created}.
                        if (res?.created) addedEmails.push(email);
                    }
                    if (addedEmails.length) {
                        // Refetch to get the correct enriched rows (including invited status).
                        refetchUsers();
                        setLastAddedName(addedEmails.join(', '));
                        setBarVisible(true);
                    }
                    if (failures.length) {
                        setAddUserError(failures.join('; '));
                        return;
                    }
                    setIsAddModalOpen(false);
                }}
                errorMessage={addUserError}
            />

            <SparkModalPortal
                open={rolesModalOpen}
                onClose={() => setRolesModalOpen(false)}
                size="slot"
                className="settings-roles-modal-portal"
            >
                <Modal
                    open
                    title="Roles & Permission Matrix"
                    showSubtitle={false}
                    showOverlay={false}
                    showMainButton={false}
                    tertiaryLabel="Close"
                    onTertiaryClick={() => setRolesModalOpen(false)}
                    onCloseClick={() => setRolesModalOpen(false)}
                >
                    <RolesPermissionsMatrix />
                </Modal>
            </SparkModalPortal>

            {removeModalOpen ? (
                <SparkModalPortal
                    open
                    onClose={() => setRemoveModalOpen(false)}
                    size="text"
                >
                    <ModalFeedback
                        feedbackType="warning"
                        title="Remove user?"
                        content="Removing this user will revoke their membership in this workspace. Their account is not deleted and they may be re-invited at any time."
                        showOverlay={false}
                        danger
                        primaryLabel="Remove user"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={confirmRemoveUser}
                        onTertiaryClick={() => setRemoveModalOpen(false)}
                        onCloseClick={() => setRemoveModalOpen(false)}
                    />
                </SparkModalPortal>
            ) : null}

            {selfDemoteUser ? (
                <SparkModalPortal
                    open
                    onClose={() => { setSelfDemoteUser(null); setSelfDemoteRole(null); }}
                    size="text"
                >
                    <ModalFeedback
                        feedbackType="warning"
                        title="Change your role?"
                        content="You are about to remove your Admin permissions. You will lose access to managing users and certain workspace settings. Do you want to continue?"
                        showOverlay={false}
                        danger
                        primaryLabel="Change role"
                        tertiaryLabel="Keep Admin"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={() => {
                            const u = selfDemoteUser;
                            const nextRole = selfDemoteRole;
                            setSelfDemoteUser(null);
                            setSelfDemoteRole(null);
                            requestSetRole(u, nextRole, true);
                        }}
                        onTertiaryClick={() => { setSelfDemoteUser(null); setSelfDemoteRole(null); }}
                        onCloseClick={() => { setSelfDemoteUser(null); setSelfDemoteRole(null); }}
                    />
                </SparkModalPortal>
            ) : null}
        </>
    );
}
